/**
 * YouTube Music CSV Parser
 * Parses the tracks.csv file from Google Takeout > YouTube and YouTube Music > music-library-songs
 */

export interface Track {
  title: string;
  artist: string;
  album: string;
  duration: string;
  rating: string;
  playCount: number;
  removed: boolean;
}

/**
 * Parse the YouTube Music tracks.csv file
 * Expected format: Title,Album,Artist,Duration (seconds),Rating,Play Count,Removed
 */
export async function parseYouTubeMusicCSV(file: File): Promise<Track[]> {
  const text = await file.text();
  const lines = text.split('\n').filter(line => line.trim());
  
  if (lines.length === 0) {
    throw new Error('CSV file is empty');
  }

  // Skip header line
  const dataLines = lines.slice(1);
  const tracks: Track[] = [];

  for (const line of dataLines) {
    try {
      const track = parseCSVLine(line);
      if (track) {
        tracks.push(track);
      }
    } catch (error) {
      console.warn('Failed to parse line:', line, error);
      // Continue with other lines
    }
  }

  if (tracks.length === 0) {
    throw new Error('No valid tracks found in CSV file');
  }

  return tracks;
}

/**
 * Parse a single CSV line, handling quoted fields with commas
 */
function parseCSVLine(line: string): Track | null {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // Field separator
      fields.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  // Add last field
  fields.push(current.trim());

  // Validate field count (should be 7)
  if (fields.length < 3) {
    console.warn('Invalid CSV line - not enough fields:', line);
    return null;
  }

  const [title, album, artist, duration = '', rating = '', playCount = '0', removed = ''] = fields;

  // Skip tracks without essential metadata
  if (!title || !artist) {
    return null;
  }

  return {
    title,
    artist,
    album: album || 'Unknown Album',
    duration,
    rating,
    playCount: parseInt(playCount, 10) || 0,
    removed: removed.toLowerCase() === 'yes' || removed.toLowerCase() === 'true',
  };
}

/**
 * Filter out tracks that are not useful for album matching
 */
export function filterUsefulTracks(tracks: Track[]): Track[] {
  return tracks.filter(track => {
    // Skip removed tracks
    if (track.removed) return false;

    // Skip tracks with missing album info
    if (!track.album || track.album === 'Unknown Album') return false;

    // Skip obvious playlists or compilations
    const albumLower = track.album.toLowerCase();
    if (
      albumLower.includes('playlist') ||
      albumLower.includes('mix') ||
      albumLower.includes('radio') ||
      albumLower.includes('various artists')
    ) {
      return false;
    }

    return true;
  });
}

/**
 * Group tracks by album
 */
export function groupByAlbum(tracks: Track[]): Map<string, Track[]> {
  const albumMap = new Map<string, Track[]>();

  for (const track of tracks) {
    // Create a normalized key: "artist - album" in lowercase
    const key = `${track.artist.toLowerCase()} - ${track.album.toLowerCase()}`;
    
    if (!albumMap.has(key)) {
      albumMap.set(key, []);
    }
    
    albumMap.get(key)!.push(track);
  }

  return albumMap;
}

/**
 * Get unique albums from tracks
 */
export function getUniqueAlbums(tracks: Track[]): Array<{ artist: string; album: string; trackCount: number }> {
  const albumMap = groupByAlbum(tracks);
  const uniqueAlbums: Array<{ artist: string; album: string; trackCount: number }> = [];

  for (const [key, albumTracks] of albumMap.entries()) {
    // Use the first track's metadata (they should all be the same for the album)
    const firstTrack = albumTracks[0];
    uniqueAlbums.push({
      artist: firstTrack.artist,
      album: firstTrack.album,
      trackCount: albumTracks.length,
    });
  }

  // Sort by track count descending (albums with more tracks first)
  uniqueAlbums.sort((a, b) => b.trackCount - a.trackCount);

  return uniqueAlbums;
}
